// Quotes / Invoices / Credit notes - 1:1 with Schema_Specification Section 3.5.
// Backs Quotes (2.5.x) and Invoices & Credit notes (2.6.x). Reuses Client +
// SendDocumentRequest + PdfLink from the existing type modules.
import i18n from '@/i18n'
import type { Client } from './clients'
import type { ListQuery } from './index'

// --- Line items (shared by quotes + invoices) ---
export interface LineItem {
  id?: string
  service_item_id?: string | null
  description: string
  service_description?: string | null
  image_file_id?: string | null
  quantity: number
  // Undefined is the editable-form state for a required price that has not
  // been entered yet. Persisted quote/invoice lines always contain a number.
  unit_price: number | undefined
  tax_rate: number
  line_total: number
  // Signed display URL resolved from the persisted image_file_id snapshot.
  image_url?: string | null
}

// --- Surcharges / optional charges (2.5.x, feedback #29) ---
// Wire shape persisted to quote_surcharges. The OptionalCharge shared type
// (components/shared/types) is structurally identical and is used by the editor.
export type QuoteSurchargeType =
  | 'emergency'
  | 'after_hours'
  | 'weekend'
  | 'holiday'
  | 'distance'
  | 'custom'

export interface Surcharge {
  id?: string
  type: QuoteSurchargeType
  description?: string
  image_file_id?: string | null
  image_url?: string | null
  amount: number
  tax_rate?: number
}

export interface OptionalFeeTemplate {
  id: string
  type: QuoteSurchargeType
  name: string
  description: string
  image_file_id?: string | null
  image_url?: string | null
  icon_key?: 'bus' | 'thunder' | 'calendar' | null
  amount: number
  tax_rate: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CreateOptionalFeeTemplateRequest {
  name: string
  amount: number
  is_active?: boolean
  image_file_id?: string | null
}

export interface OptionalFeeImageUploadResponse {
  id: string
  url: string | null
}

export type TradeDiscountType = 'percent' | 'fixed'

// --- Quote / document attachments (feedback #31/#32) ---
// Mirrors the backend DocumentableResource wire shape.
export interface QuoteDocument {
  id: string
  file_id: string
  doc_type: string | null
  file_name: string | null
  file_type: string | null
  file_size: number | null
  url: string | null
  uploaded_by_name: string | null
  upload_date: string | null
  created_at: string
}

// --- Quotes (2.5.x) ---
// Wire values for App\Const\QuoteStatus — single runtime source of truth.
export const QUOTE_STATUS = {
  DRAFT: 'draft',
  SENT: 'sent',
  WON: 'won',
  LOST: 'lost',
} as const

export type QuoteStatus = (typeof QUOTE_STATUS)[keyof typeof QUOTE_STATUS]

export interface Quote {
  id: string
  tenant_id: string
  number: string | null
  client_id: string
  // 2.3.18 - the job this quote was raised from (post-inspection quote); null
  // for standalone quotes.
  job_id?: string | null
  job_reference?: string | null
  issue_date: string | null
  valid_until: string | null
  status: QuoteStatus
  subtotal: number
  tax_total: number
  surcharge_total?: number
  discount_type?: TradeDiscountType | null
  discount_value?: number
  discount_amount?: number
  total: number
  notes: string | null
  request_signature?: boolean
  allow_online_payment?: boolean
  signature_request_id: string | null
  sent_at: string | null
  accepted_at: string | null
  converted_invoice_id?: string | null
  converted_contract_id?: string | null
  converted_at?: string | null
  lifecycle?: {
    sent_recipient?: string | null
  }
  created_at: string
  updated_at: string
  // Derived/list-only fields surfaced by the backend for display.
  client?: Client | null
  client_name?: string | null
  technician_name?: string | null
  source?: 'operator' | 'technician' | null
  title?: string | null
}

export interface QuoteDetail extends Quote {
  client: Client
  items: LineItem[]
  surcharges?: Surcharge[]
  documents?: QuoteDocument[]
}

export interface QuoteHistory {
  id: string
  tenant_id: string
  quote_id: string
  event_type: string
  description: string | null
  channel: 'Portal' | 'Email' | 'YouSign' | 'System' | string
  portal_status: 'Draft' | 'Sent' | 'Won' | 'Lost' | null
  properties: Record<string, unknown> | null
  causer_id: string | null
  user: string | null
  timestamp: string
  created_at: string
}

export interface CreateQuoteRequest {
  client_id: string
  title?: string | null
  issue_date?: string
  valid_until?: string
  // On create this is usually 'draft'; 'sent' is still accepted for API
  // compatibility. On edit (2.5.4) the operator can manually set one of the 4
  // canonical statuses via the Status dropdown.
  // Omitted on an edit that must keep the quote's current status (e.g. "Save
  // and Send", where POST /quotes/:id/send performs the transition).
  status?: QuoteStatus
  items: LineItem[]
  surcharges?: Surcharge[]
  discount_type?: TradeDiscountType | null
  discount_value?: number
  notes?: string
  job_id?: string
  technician_id?: string
  request_signature?: boolean
  allow_online_payment?: boolean
}
export type EditQuoteRequest = Partial<CreateQuoteRequest>

export interface QuoteListQuery extends ListQuery {
  status?: string | string[]
  quote_group?: string
  client_id?: string
  date_from?: string
  date_to?: string
}

export interface QuoteKpiBucket {
  count: number
  total: number
}

// Quote list KPI summary cards (CR 86eyqfa58).
export interface QuoteSummary {
  waiting: QuoteKpiBucket
  expires_soon: QuoteKpiBucket
  expired: QuoteKpiBucket
  won: QuoteKpiBucket
  signature_rate: {
    percentage: number
    won_count: number
    issued_count: number
  }
}

export interface ConvertToContractRequest {
  start_date: string
  end_date: string
  terms?: string
}

// --- Invoices (2.6.x) ---
export type InvoiceStatus =
  | 'pro_forma'
  | 'pro_forma_sent'
  | 'validated'
  | 'pending'
  | 'paid'
  | 'partially_paid'
  | 'overdue'
  | 'cancelled_via_credit_note'

export interface Invoice {
  id: string
  tenant_id: string
  number: string | null
  title?: string | null
  client_id: string
  quote_id: string | null
  job_id: string | null
  contract_id?: string | null
  issue_date: string | null
  due_date: string | null
  payment_deadline_days?: number | null
  purchase_order?: string | null
  status: InvoiceStatus
  subtotal: number
  tax_total: number
  surcharge_total?: number
  discount_type?: 'percent' | 'fixed' | null
  discount_value?: number
  discount_amount?: number
  total: number
  amount_paid: number
  /** Actual settled payment amount, excluding credit notes (detail endpoint). */
  paid_payment_total?: number
  /** Cumulative amount of all credit notes linked to this invoice. */
  credit_note_total?: number
  /** Outstanding amount after paid payments and credit notes. */
  balance_due?: number
  /** Maximum amount allowed for the next credit note. */
  max_credit_note_amount?: number
  allow_online_payment?: boolean
  notes: string | null
  sent_at: string | null
  paid_at: string | null
  created_at: string
  updated_at: string
  // Derived/list-only display fields.
  client?: Client | null
  client_name?: string | null
  job_reference?: string | null
  payment_method?: string | null
}

export interface InvoiceDetail extends Invoice {
  payments_count: number
  client: Client
  contract?: { id: string; number: string | null; title?: string | null } | null
  items: LineItem[]
  surcharges?: Surcharge[]
  documents?: QuoteDocument[]
  lifecycle?: {
    validated_at?: string | null
    sent_recipient?: string | null
  } | null
}

export interface CreateInvoiceRequest {
  client_id: string
  quote_id?: string
  job_id?: string | null
  contract_id?: string
  title?: string | null
  issue_date?: string
  due_date?: string
  payment_deadline_days?: number
  purchase_order?: string
  items: LineItem[]
  surcharges?: Surcharge[]
  discount_type?: 'percent' | 'fixed' | null
  discount_value?: number
  notes?: string
  allow_online_payment?: boolean
}
export type EditInvoiceRequest = Partial<CreateInvoiceRequest>

export interface InvoiceListQuery extends ListQuery {
  status?: string | string[]
  // A single client id or several (multi-client filter on the invoice list).
  client_id?: string | string[]
  date_from?: string
  date_to?: string
}

export interface InvoiceSummary {
  total: number
  paid: { count: number; total: number }
  pending: { count: number; total: number }
  overdue: { count: number; total: number }
}

export interface BulkSendRequest {
  invoice_ids: string[]
}

export interface BulkSendInvoicesResponse {
  sent: string[]
  failed: Array<{ id: string; reason: string }>
}

// 2.6.6 "send all unpaid": the action is queued server-side (chunked jobs) to
// avoid request timeouts, so the response only reports how many past-due
// invoices were queued — there is no synchronous per-invoice result.
export interface BulkSendUnpaidResponse {
  queued: number
}

// Bulk row-action requests (feedback #37). A simple array of invoice ids.
export interface BulkActionRequest {
  invoice_ids: string[]
}

// Per-id result map returned by bulk-send / bulk-chase / bulk-mark-paid (#37).
export interface BulkActionResult {
  succeeded?: string[]
  sent?: string[]
  failed?: { id: string; reason: string }[]
}

// Unpaid-by-client aggregation row (feedback #36).
export interface UnpaidByClientRow {
  client_id: string
  client_name: string | null
  unpaid_count: number
  total_outstanding: number
  oldest_overdue_date: string | null
}

// --- Credit notes (2.6.9-2.6.12) ---
export type CreditNoteReason =
  | 'commercial_gesture'
  | 'overpayment'
  | 'refund'
  | 'billing_error'
  | 'service_canceled'
  | 'exceptional_discount'
  | 'other'

export interface CreditNote {
  id: string
  tenant_id: string
  number: string | null
  invoice_id: string | null
  client_id: string
  issue_date: string | null
  status: string
  subtotal?: number
  tax_total?: number
  total: number
  reason: string | null
  sent_at: string | null
  created_at: string
  updated_at: string
  client?: Client | null
  client_name?: string | null
  invoice_number?: string | null
  items?: LineItem[]
}

export type CreditNoteDetail = CreditNote

// The BE computes totals from items[] (it ignores any top-level total). Feedback
// #46/#63: always submit items, never a single `total`.
export interface CreateCreditNoteRequest {
  invoice_id?: string
  client_id?: string
  issue_date?: string
  reason?: string
  items?: LineItem[]
}
export type EditCreditNoteRequest = Partial<CreateCreditNoteRequest>

export interface CreditNoteListQuery extends ListQuery {
  status?: string
  client_id?: string
  invoice_id?: string
}

// Payments: full domain types live in ./payments (Payment, PaymentDetail,
// UpdatePaymentStatusRequest, etc.) and are re-exported from the types index
// (Schema_Specification Section 3.6 / 2.7.x).

// --- Display label maps ---
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  [QUOTE_STATUS.DRAFT]: i18n.t('enums:quoteStatus.draft'),
  [QUOTE_STATUS.SENT]: i18n.t('enums:quoteStatus.sent'),
  [QUOTE_STATUS.WON]: i18n.t('enums:quoteStatus.won'),
  [QUOTE_STATUS.LOST]: i18n.t('enums:quoteStatus.lost'),
}

// Invoice statuses that represent money still owed — i.e. an invoice that can
// be chased (Pending, Partially Paid, or Overdue). Not Draft / Sent / Paid.
export const UNPAID_INVOICE_STATUSES: readonly InvoiceStatus[] = [
  'pending',
  'partially_paid',
  'overdue',
]

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  pro_forma: i18n.t('enums:invoiceStatus.pro_forma'),
  pro_forma_sent: i18n.t('enums:invoiceStatus.pro_forma_sent'),
  validated: i18n.t('enums:invoiceStatus.validated'),
  pending: i18n.t('enums:invoiceStatus.pending'),
  paid: i18n.t('enums:invoiceStatus.paid'),
  partially_paid: i18n.t('enums:invoiceStatus.partially_paid'),
  overdue: i18n.t('enums:invoiceStatus.overdue'),
  cancelled_via_credit_note: i18n.t(
    'enums:invoiceStatus.cancelled_via_credit_note'
  ),
}

// Label map keeps ALL historical reasons so legacy records saved as
// billing_error/other still render a label in lists (feedback #47).
export const CREDIT_NOTE_REASON_LABELS: Record<string, string> = {
  commercial_gesture: i18n.t('enums:creditNoteReason.commercial_gesture'),
  overpayment: i18n.t('enums:creditNoteReason.overpayment'),
  refund: i18n.t('enums:creditNoteReason.refund'),
  billing_error: i18n.t('enums:creditNoteReason.billing_error'),
  service_canceled: i18n.t('enums:creditNoteReason.service_canceled'),
  exceptional_discount: i18n.t('enums:creditNoteReason.exceptional_discount'),
  other: i18n.t('enums:creditNoteReason.other'),
}

export const CREDIT_NOTE_REASON_OPTIONS: CreditNoteReason[] = [
  'commercial_gesture',
  'billing_error',
  'service_canceled',
  'exceptional_discount',
  'other',
]
